import { Calendar, Clock, List, type LucideIcon } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { cn } from '../utils';
import { type AuctionScheduleView } from '../types';

interface ViewToggleProps {
  currentView: AuctionScheduleView;
  onViewChange: (view: AuctionScheduleView) => void;
  className?: string;
}

/**
 * View switcher — the artboard's tablist: a sunken `cf-fill` track with the
 * active pill in brand. The list comes first because it is the view the
 * artboard draws; calendar and timeline are this app's own additions.
 */
export function ViewToggle({ currentView, onViewChange, className }: ViewToggleProps) {
  const views: Array<{ key: AuctionScheduleView; label: string; icon: LucideIcon }> = [
    { key: 'list', label: t('auctionSchedule.views.list'), icon: List },
    { key: 'calendar', label: t('auctionSchedule.views.calendar'), icon: Calendar },
    { key: 'timeline', label: t('auctionSchedule.views.timelineShort'), icon: Clock },
  ];

  return (
    <div
      className={cn('flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]', className)}
      role="tablist"
    >
      {views.map(({ key, label, icon: Icon }) => {
        const active = currentView === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onViewChange(key)}
            className={cn(
              'flex h-9 items-center gap-2 rounded-cf-md px-3 text-[13px] font-semibold whitespace-nowrap',
              active ? 'bg-cf-brand text-white' : 'text-cf-ink hover:bg-cf-surface',
            )}
          >
            <Icon size={15} strokeWidth={1.75} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
